import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { computeFirstYear } from "@/lib/fees";

// Compact millions renderer: 1_500_000 → "1,5" (id-ID decimal comma)
const juta = (n) => {
  const m = Number(n) / 1_000_000;
  const rounded = Math.round(m * 10) / 10;
  return rounded.toLocaleString("id-ID", {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  });
};

/** Compact single-cell renderer for the label–value grid. Applies the 3-branch data-handling rule. */
function compactRange(min, max) {
  const hasMin = min != null && min !== "";
  const hasMax = max != null && max !== "";
  if (hasMin && hasMax) {
    if (Number(min) === Number(max)) return `Rp ${juta(min)} jt`;
    return `Rp ${juta(min)}–${juta(max)} jt`;
  }
  if (hasMin) return `Mulai Rp ${juta(min)} jt`;
  return null;
}

/**
 * BIAYA PENDIDIKAN — school-profile fee card.
 *
 * Design language mirrors the "Rincian Penilaian" card:
 *  - white background, subtle shadow, coral eyebrow
 *  - vertical label–value list with hairline dividers (label muted left, value bold right)
 *  - inline coral text-link "Lihat rincian biaya →" (no button chrome)
 *  - single-line disclaimer footer separated by hairline
 */
export default function BiayaPendidikanCard({ school }) {
  const [open, setOpen] = useState(false);
  if (!school) return null;

  const sppVal = compactRange(school.spp_min, school.spp_max);
  const entryVal = compactRange(school.entry_fee_min, school.entry_fee_max);
  const { min: fyMin, max: fyMax, hasEstimate } = computeFirstYear(school);
  const firstYearVal = hasEstimate
    ? (fyMin === fyMax ? `Rp ${juta(fyMin)} jt` : `Rp ${juta(fyMin)}–${juta(fyMax)} jt`)
    : null;
  const annualVal = Number(school.other_annual_fees) > 0 ? `± Rp ${juta(school.other_annual_fees)} jt` : null;

  const hasAnyFee = !!(sppVal || entryVal || firstYearVal || annualVal);
  const lastUpdated = school.fees_last_updated_at
    ? new Date(school.fees_last_updated_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })
    : null;

  const rows = [
    { label: "SPP bulanan", value: sppVal, suffix: "/bln", strong: true },
    { label: "Estimasi tahun pertama", value: firstYearVal, strong: true },
    { label: "Uang pangkal", value: entryVal, strong: true },
    { label: "Biaya tahunan tambahan", value: annualVal, strong: true },
  ].filter((r) => r.value);

  return (
    <div className="bg-white border border-[#EAE7E0] rounded-2xl p-6 shadow-[0_4px_18px_-4px_rgba(31,41,55,0.08)]" data-testid="biaya-pendidikan-card">
      {/* Eyebrow + academic-year badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-widest text-[#D2673E] font-semibold">Biaya Pendidikan</div>
        {school.fee_academic_year ? (
          <div className="text-[10px] px-2 py-0.5 rounded-full bg-[#F3F1ED] text-[#4B5563] border border-[#EAE7E0]" data-testid="biaya-academic-year">
            T.A. {school.fee_academic_year}
          </div>
        ) : null}
      </div>

      {/* Label–value rows with hairline dividers */}
      {rows.length > 0 ? (
        <div className="divide-y divide-[#EAE7E0]" data-testid="biaya-rows">
          {rows.map((r, i) => (
            <div key={r.label} className="flex items-baseline justify-between py-2.5" data-testid={
              i === 0 ? "biaya-row-spp"
                : i === 1 ? "biaya-row-first-year"
                : i === 2 ? "biaya-row-entry"
                : "biaya-row-annual"
            }>
              <span className="text-sm text-[#4B5563]">{r.label}</span>
              <span className="text-sm font-semibold text-[#1F2937] tabular-nums">
                {r.value}
                {r.suffix ? <span className="ml-1 text-xs text-[#4B5563] font-normal">{r.suffix}</span> : null}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-[#4B5563] italic py-2" data-testid="biaya-empty">
          Informasi biaya belum tersedia
        </div>
      )}

      {/* Detail link — coral text, not an outline button */}
      {hasAnyFee ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button type="button"
              className="mt-4 text-sm text-[#D2673E] hover:text-[#B85838] font-semibold inline-flex items-center gap-1 transition-colors"
              data-testid="biaya-detail-link">
              Lihat rincian biaya <span aria-hidden>→</span>
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="biaya-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif-display">Rincian Biaya Pendidikan</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm divide-y divide-[#EAE7E0]">
              <DetailRow label="Uang pangkal (min)" value={school.entry_fee_min} />
              <DetailRow label="Uang pangkal (max)" value={school.entry_fee_max} />
              <DetailRow label="SPP / bulan (min)" value={school.spp_min} />
              <DetailRow label="SPP / bulan (max)" value={school.spp_max} />
              <DetailRow label="Biaya tahunan lain" value={school.other_annual_fees} />
              <DetailRow label="Estimasi tahun pertama (min)" value={fyMin} highlight />
              <DetailRow label="Estimasi tahun pertama (max)" value={fyMax} highlight />
            </div>
            {Array.isArray(school.fee_structure) && school.fee_structure.length > 0 ? (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-[#D2673E] font-semibold mb-2">Per Jenjang</div>
                <table className="w-full text-xs">
                  <thead className="text-left text-[#4B5563]">
                    <tr><th className="py-1">Jenjang</th><th className="text-right py-1">Uang pangkal</th><th className="text-right py-1">SPP / bulan</th></tr>
                  </thead>
                  <tbody>
                    {school.fee_structure.map((r, i) => (
                      <tr key={i} className="border-t border-[#EAE7E0]" data-testid={`fee-structure-row-${i}`}>
                        <td className="py-1.5">{r.jenjang || "—"}</td>
                        <td className="py-1.5 text-right">{r.uang_pangkal ? `Rp ${Number(r.uang_pangkal).toLocaleString("id-ID")}` : "—"}</td>
                        <td className="py-1.5 text-right">{r.spp_bulanan ? `Rp ${Number(r.spp_bulanan).toLocaleString("id-ID")}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      ) : null}

      {/* Footer — hairline divider + italic disclaimer */}
      <div className="mt-4 pt-3 border-t border-[#EAE7E0] text-[11px] text-[#4B5563] italic leading-snug" data-testid="biaya-disclaimer">
        Estimasi 12 bulan SPP. Konfirmasi ke sekolah untuk biaya terbaru
        {lastUpdated ? <> · diperbarui {lastUpdated}</> : null}.
      </div>
    </div>
  );
}

function DetailRow({ label, value, highlight = false }) {
  return (
    <div className={"flex items-center justify-between py-2 " + (highlight ? "text-[#D2673E] font-semibold" : "text-[#1F2937]")}>
      <span className={highlight ? "" : "text-[#4B5563]"}>{label}</span>
      <span className="tabular-nums">{value != null && value !== "" ? `Rp ${Number(value).toLocaleString("id-ID")}` : <em className="text-[#4B5563] font-normal not-italic">—</em>}</span>
    </div>
  );
}
